// web/src/pages/jobs/NewJobWizard.tsx
//
// J-3 — 3-step "New job" wizard (Client & Basis → Items → Review & Create).
//
// Plan I scope: no BOM domain, items are hand-keyed via JobItemsGrid.
// The wizard follows the same manual step-state pattern as NewInvoiceWizard.tsx.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { JobItemsGrid, blankRow, rowToRequest, type JobItemRow } from "@/components/job-items-grid";
import { PageContainer } from "@/app/layouts/PageContainer";
import { IntentButton } from "@/components/intent-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { jobsApi, type CreateJobRequest } from "@/lib/jobs";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Basis state shape
// ---------------------------------------------------------------------------

interface BasisState {
  clientId: number | null;
  branchId: number | null;
  ownerEmployeeId: number | null;
  expectedDispatchDate: string;
  customerReference: string;
  purchaseOrderNumber: string;
  description: string;
}

const EMPTY_BASIS: BasisState = {
  clientId: null,
  branchId: null,
  ownerEmployeeId: null,
  expectedDispatchDate: "",
  customerReference: "",
  purchaseOrderNumber: "",
  description: "",
};

// ---------------------------------------------------------------------------
// NewJobWizard
// ---------------------------------------------------------------------------

export function NewJobWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 state
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [basis, setBasis] = useState<BasisState>(EMPTY_BASIS);

  // Step 2 state
  const [items, setItems] = useState<JobItemRow[]>([]);

  // Step 3 state
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const itemsValid = useMemo(
    () => items.length === 0 || items.every((r) => rowToRequest(r) !== undefined),
    [items],
  );

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function patchBasis(patch: Partial<BasisState>) {
    setBasis((b) => ({ ...b, ...patch }));
  }

  function buildPayload(): CreateJobRequest {
    return {
      clientId: client?.clientId ?? null,
      branchId: basis.branchId,
      ownerEmployeeId: basis.ownerEmployeeId,
      expectedDispatchDate: basis.expectedDispatchDate || null,
      customerReference: basis.customerReference.trim() || null,
      purchaseOrderNumber: basis.purchaseOrderNumber.trim() || null,
      description: basis.description.trim() || null,
      items: items.length > 0
        ? items.map((r, i) => ({ ...rowToRequest(r)!, sortOrder: i }))
        : null,
    };
  }

  // ---------------------------------------------------------------------------
  // Mutation
  // ---------------------------------------------------------------------------

  const createMut = useMutation({
    mutationFn: (body: CreateJobRequest) => jobsApi.create(body),
    onSuccess: (job) => {
      toast.success(`Job ${job.jobReferenceNo} created.`);
      navigate(`/jobs/${job.jobId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  function onSubmit() {
    setSubmitError(null);
    createMut.mutate(buildPayload());
  }

  // ---------------------------------------------------------------------------
  // Step breadcrumb labels
  // ---------------------------------------------------------------------------

  const STEP_LABELS = ["1. Client & Basis", "2. Items", "3. Review & Create"];

  return (
    <PageContainer>
      <h1 className="mb-1 text-2xl font-semibold">New job</h1>
      <ol className="mb-6 flex gap-3 text-sm">
        {STEP_LABELS.map((label, i) => (
          <li key={label} className={step === i + 1 ? "font-semibold" : "text-muted-foreground"}>
            {label}
          </li>
        ))}
      </ol>

      {/* ------------------------------------------------------------------ */}
      {/* Step 1 — Client & Basis                                             */}
      {/* ------------------------------------------------------------------ */}
      {step === 1 && (
        <section aria-label="Step 1: Client & Basis" className="space-y-4 max-w-xl">
          <div>
            <Label>Client (optional)</Label>
            <ClientPicker value={client} onChange={setClient} />
          </div>

          <div>
            <Label htmlFor="job-expected-dispatch">Expected dispatch date</Label>
            <Input
              id="job-expected-dispatch"
              type="date"
              value={basis.expectedDispatchDate}
              onChange={(e) => patchBasis({ expectedDispatchDate: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="job-customer-ref">Customer reference (optional)</Label>
            <Input
              id="job-customer-ref"
              value={basis.customerReference}
              onChange={(e) => patchBasis({ customerReference: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="job-po">PO # (optional)</Label>
            <Input
              id="job-po"
              value={basis.purchaseOrderNumber}
              onChange={(e) => patchBasis({ purchaseOrderNumber: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="job-desc">Description (optional)</Label>
            <textarea
              id="job-desc"
              className="block w-full rounded-md border p-2 text-sm"
              rows={3}
              value={basis.description}
              onChange={(e) => patchBasis({ description: e.target.value })}
            />
          </div>

          <div className="flex justify-end">
            <IntentButton onClick={() => setStep(2)}>Next</IntentButton>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 2 — Items                                                      */}
      {/* ------------------------------------------------------------------ */}
      {step === 2 && (
        <section aria-label="Step 2: Items" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Add line items by hand. Items are optional — you can add them later on the job detail page.
          </p>

          <JobItemsGrid items={items} onChange={setItems} editable />

          {items.length === 0 && (
            <IntentButton intent="secondary" onClick={() => setItems([blankRow(0)])}>
              Add first item
            </IntentButton>
          )}

          <div className="flex justify-between">
            <IntentButton intent="ghost" onClick={() => setStep(1)}>Back</IntentButton>
            <IntentButton disabled={!itemsValid} onClick={() => setStep(3)}>Next</IntentButton>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Step 3 — Review & Create                                            */}
      {/* ------------------------------------------------------------------ */}
      {step === 3 && (
        <section aria-label="Step 3: Review & Create" className="space-y-4 max-w-2xl">
          <div className="rounded-md border p-4">
            <h2 className="mb-3 font-semibold">Summary</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Client</dt>
              <dd>{client?.fullName ?? "(none)"}</dd>
              <dt className="text-muted-foreground">Expected dispatch</dt>
              <dd>{basis.expectedDispatchDate || "—"}</dd>
              <dt className="text-muted-foreground">Customer reference</dt>
              <dd>{basis.customerReference || "—"}</dd>
              <dt className="text-muted-foreground">PO #</dt>
              <dd>{basis.purchaseOrderNumber || "—"}</dd>
              <dt className="text-muted-foreground">Description</dt>
              <dd>{basis.description || "—"}</dd>
              <dt className="text-muted-foreground">Items</dt>
              <dd>{items.length === 0 ? "(none)" : `${items.length} line${items.length === 1 ? "" : "s"}`}</dd>
            </dl>

            {items.length > 0 && (
              <table className="mt-4 w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left">Description</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Unit ex GST</th>
                    <th className="text-right">GST rate</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r._key}>
                      <td>{r.description || <em className="text-muted-foreground">(blank)</em>}</td>
                      <td className="text-right">{r.quantity}</td>
                      <td className="text-right">{r.unitPriceExGst}</td>
                      <td className="text-right">{r.gstRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {submitError && (
            <p className="text-sm text-destructive" role="alert">{submitError}</p>
          )}

          <div className="flex justify-between">
            <IntentButton intent="ghost" onClick={() => setStep(2)}>Back</IntentButton>
            <IntentButton onClick={onSubmit} loading={createMut.isPending}>
              Create Job
            </IntentButton>
          </div>
        </section>
      )}
    </PageContainer>
  );
}
